// src/pages/ManageDepts.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";

const ACCESS_NONE = "none";
const ACCESS_READ = "read";
const ACCESS_EDIT = "edit";

// ── Assign Supervisors modal ──────────────────────────────────────────────────
function AssignSupervisorsModal({ dept, supervisors, onSave, onClose }) {
  const allSupervisors = supervisors.filter(s => s.role === "supervisor");

  // Build initial access map: email → "edit" | "read" | "none"
  const initialAccess = () => {
    const map = {};
    for (const s of allSupervisors) {
      if ((dept.editSupervisors||[]).includes(s.email)) map[s.email] = ACCESS_EDIT;
      else if ((dept.readSupervisors||[]).includes(s.email)) map[s.email] = ACCESS_READ;
      else map[s.email] = ACCESS_NONE;
    }
    return map;
  };

  const [access,  setAccess]  = useState(initialAccess);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  function setLevel(email, level) {
    setAccess(prev => ({ ...prev, [email]: level }));
  }

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      const editSupervisors = Object.entries(access).filter(([,v])=>v===ACCESS_EDIT).map(([e])=>e);
      const readSupervisors = Object.entries(access).filter(([,v])=>v===ACCESS_READ).map(([e])=>e);

      const res = await fetch(`/api/categories/${dept.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: dept.name, editSupervisors, readSupervisors }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      onSave(d.category);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const editCount = Object.values(access).filter(v=>v===ACCESS_EDIT).length;
  const readCount = Object.values(access).filter(v=>v===ACCESS_READ).length;

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <h2>Assign Supervisors — {dept.name}</h2>
          <button className="btn-ghost" onClick={onClose}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="18">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize:13, color:"var(--gray-500)", marginBottom:12, lineHeight:1.6 }}>
            Set each supervisor's access level for <strong>{dept.name}</strong>.
          </p>

          {/* Legend */}
          <div style={{ display:"flex", gap:16, marginBottom:16, fontSize:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:"#1F2937", display:"inline-block" }}/>
              <span style={{ color:"var(--gray-600)", fontWeight:600 }}>Edit Access</span>
              <span style={{ color:"var(--gray-400)" }}>— can create, edit and design courses</span>
            </div>
          </div>
          <div style={{ display:"flex", gap:16, marginBottom:20, fontSize:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:"#6366F1", display:"inline-block" }}/>
              <span style={{ color:"var(--gray-600)", fontWeight:600 }}>Read-Only</span>
              <span style={{ color:"var(--gray-400)" }}>— can view courses, stats and completion data</span>
            </div>
          </div>

          {allSupervisors.length === 0 ? (
            <div style={{ textAlign:"center", padding:"20px 0", color:"var(--gray-400)", fontSize:13 }}>
              No supervisors found. Add users with the Supervisor role first.
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:20 }}>
              {/* Column headers */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 90px 90px 90px", gap:8, padding:"0 14px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--gray-400)" }}>
                <span>Supervisor</span>
                <span style={{ textAlign:"center" }}>No Access</span>
                <span style={{ textAlign:"center" }}>Read-Only</span>
                <span style={{ textAlign:"center" }}>Edit</span>
              </div>

              {allSupervisors.map(s => {
                const level = access[s.email] || ACCESS_NONE;
                return (
                  <div key={s.email} style={{
                    display:"grid", gridTemplateColumns:"1fr 90px 90px 90px", gap:8, alignItems:"center",
                    padding:"10px 14px", borderRadius:"var(--radius)",
                    background: level === ACCESS_EDIT ? "#E8EDF2" : level === ACCESS_READ ? "#EEF2FF" : "var(--gray-50)",
                    border: `1.5px solid ${level === ACCESS_EDIT ? "#1F2937" : level === ACCESS_READ ? "#6366F1" : "var(--gray-200)"}`,
                    transition:"all 0.12s",
                  }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:600, color:"var(--gray-900)" }}>{s.name || s.email}</div>
                      <div style={{ fontSize:12, color:"var(--gray-500)" }}>{s.email}</div>
                    </div>
                    {[ACCESS_NONE, ACCESS_READ, ACCESS_EDIT].map(lvl => (
                      <label key={lvl} style={{ display:"flex", justifyContent:"center", cursor:"pointer" }}>
                        <input
                          type="radio"
                          name={`access_${s.email}`}
                          checked={level === lvl}
                          onChange={() => setLevel(s.email, lvl)}
                          style={{ width:"auto", accentColor: lvl === ACCESS_EDIT ? "#1F2937" : lvl === ACCESS_READ ? "#6366F1" : "#9CA3AF", width:16, height:16 }}
                        />
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary */}
          {allSupervisors.length > 0 && (
            <div style={{ fontSize:12, color:"var(--gray-500)", marginBottom:12 }}>
              {editCount} with edit access · {readCount} with read-only · {allSupervisors.length - editCount - readCount} with no access
            </div>
          )}

          {error && <div style={{ fontSize:13, color:"var(--red)", marginBottom:8 }}>{error}</div>}

          <div className="form-actions">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || allSupervisors.length === 0}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ManageDepts() {
  const { user }      = useAuth();
  const [depts,       setDepts]       = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [newName,     setNewName]     = useState("");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);
  const [assigning,   setAssigning]   = useState(null); // dept being assigned

  async function load() {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([
        fetch("/api/categories").then(r => r.json()),
        fetch("/api/users?role=supervisor").then(r => r.json()),
      ]);
      setDepts(d.categories || []);
      setSupervisors(u.users || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await fetch("/api/categories", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setDepts(prev => [...prev, d.category].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName("");
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(dept) {
    if (!window.confirm(`Delete category "${dept.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`/api/categories/${dept.id}`, { method: "DELETE" });
      setDepts(prev => prev.filter(d => d.id !== dept.id));
    } catch (e) { alert(e.message); }
  }

  function handleAssignSaved(updatedCategory) {
    setDepts(prev => prev.map(d => d.id === updatedCategory.id ? updatedCategory : d));
    setAssigning(null);
  }

  function supervisorSummary(dept) {
    const editCount = (dept.editSupervisors||[]).length;
    const readCount = (dept.readSupervisors||[]).length;
    if (!editCount && !readCount) return null;
    const parts = [];
    if (editCount) parts.push(`${editCount} edit`);
    if (readCount) parts.push(`${readCount} read-only`);
    return parts.join(", ");
  }

  function supervisorNames(dept) {
    const editNames = (dept.editSupervisors||[]).map(e => {
      const s = supervisors.find(u => u.email === e);
      return s ? (s.name || s.email) : e;
    });
    const readNames = (dept.readSupervisors||[]).map(e => {
      const s = supervisors.find(u => u.email === e);
      return s ? (s.name || s.email) : e;
    });
    return { editNames, readNames };
  }

  if (user?.role !== "admin") return <div className="card"><p>Admin access required.</p></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Categories</div>
          <div className="page-subtitle">Manage course categories and who can manage them</div>
        </div>
      </div>

      {/* Add category */}
      <div className="card" style={{ marginBottom: 20, padding: "20px 24px" }}>
        <form onSubmit={handleAdd} style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
          <div className="form-group" style={{ flex: 1, margin: 0 }}>
            <label>New Category Name</label>
            <input
              value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Academy, Commercial, First Team…"
            />
          </div>
          <button className="btn-primary" type="submit" disabled={saving || !newName.trim()} style={{ flexShrink: 0 }}>
            {saving ? "Adding…" : "+ Add Category"}
          </button>
        </form>
        {error && <div style={{ fontSize: 13, color: "var(--red)", marginTop: 8 }}>{error}</div>}
      </div>

      {/* Category list */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="page-loading"><span className="spinner" style={{ width: 24, height: 24 }}/></div>
        ) : depts.length === 0 ? (
          <div className="empty-state"><h3>No categorys yet</h3><p>Add your first category above.</p></div>
        ) : (
          <table className="contacts-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Supervisors</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {depts.map(d => (
                <tr key={d.id} className="contact-row">
                  <td className="contact-cell">
                    <div className="contact-name">{d.name}</div>
                  </td>
                  <td className="contact-cell">
                    {!supervisorSummary(d) ? (
                      <span style={{ fontSize:13, color:"var(--gray-400)" }}>None assigned</span>
                    ) : (
                      <div style={{ fontSize:13 }}>
                        {(d.editSupervisors||[]).length > 0 && (
                          <div style={{ marginBottom:3 }}>
                            <span style={{ fontSize:11, fontWeight:700, color:"#1F2937", background:"#E8EDF2", padding:"1px 7px", borderRadius:99, marginRight:6 }}>
                              Edit
                            </span>
                            <span style={{ color:"var(--gray-600)" }}>{supervisorNames(d).editNames.join(", ")}</span>
                          </div>
                        )}
                        {(d.readSupervisors||[]).length > 0 && (
                          <div>
                            <span style={{ fontSize:11, fontWeight:700, color:"#4338CA", background:"#EEF2FF", padding:"1px 7px", borderRadius:99, marginRight:6 }}>
                              Read-Only
                            </span>
                            <span style={{ color:"var(--gray-600)" }}>{supervisorNames(d).readNames.join(", ")}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="contact-cell" style={{ fontSize: 13, color: "var(--gray-500)" }}>
                    {d.createdAt ? new Date(d.createdAt).toLocaleDateString("en-GB") : "—"}
                  </td>
                  <td className="contact-cell" style={{ textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button className="btn-secondary" style={{ fontSize: 12, padding: "4px 12px" }}
                        onClick={() => setAssigning(d)}>
                        Assign Supervisors
                      </button>
                      <button className="btn-ghost" style={{ fontSize: 12, color: "var(--red)" }}
                        onClick={() => handleDelete(d)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {assigning && (
        <AssignSupervisorsModal
          dept={assigning}
          supervisors={supervisors}
          onSave={handleAssignSaved}
          onClose={() => setAssigning(null)}
        />
      )}
    </div>
  );
}
